import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PipelineControlService } from '../../../core/render-runtime/pipeline-control.service';
import { SafeErrorService } from '../../../core/services/safe-error.service';
import { PipelineLoaderService } from './pipeline-loader.service';
import { JivuCommandService } from '../../../features/JivuCommand/services/jivu-command.service';

@Component({
  selector: 'app-pipeline-loader-popup',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './pipeline-loader-popup.component.html',
  styleUrl: './pipeline-loader-popup.component.scss'
})
export class PipelineLoaderPopupComponent {
  readonly loader = inject(PipelineLoaderService);
  private readonly pipelineControl = inject(PipelineControlService);
  private readonly safeError = inject(SafeErrorService);
  private readonly jivuCommand = inject(JivuCommandService);
  readonly state = this.loader.state;
  private controlBusy = false;

  /** Loader-context scopes (= Jivu command types) for the 5 command-driven flows. */
  private static readonly COMMAND_FLOW_SCOPES = new Set([
    'smart_video_render', 'processing_upload_render', 'review_render_or_finalize',
    'template_use_render', 'asset_sequence_render'
  ]);
  /** Loader-context pipelines for command-driven flows (NOT 'socialpost' — Social keeps its popup). */
  private static readonly COMMAND_FLOW_PIPELINES = new Set(['smartvideo', 'processing']);

  /**
   * The loader popup is RETIRED for Jivu Command flows — the Command Center (with live %) is the
   * surface. We identify a command flow by its loader CONTEXT (scope = the command type, or a
   * command pipeline), set at start() time, so the popup stays hidden in EVERY state: running,
   * error, AND complete. (Keying on "a command is currently running" was too narrow — it flipped
   * off the instant the command ended/was cancelled, letting the error/success popup leak through;
   * that is the bug this fixes.) Social / PostGen use 'SocialPost' / their own pipeline, so they are
   * NOT matched and keep their popup. Only this component's RENDER is gated — the
   * PipelineLoaderService and runtime store stay fully intact (the "already running" guard, Generate
   * reset, dashboard widget, and beforeunload warning all keep working).
   */
  readonly suppressForCommand = computed(() => {
    const ctx = this.state().context;
    const scope = String(ctx?.scope || '').toLowerCase();
    const pipeline = String(ctx?.pipeline || '').toLowerCase();
    if (PipelineLoaderPopupComponent.COMMAND_FLOW_SCOPES.has(scope)
      || PipelineLoaderPopupComponent.COMMAND_FLOW_PIPELINES.has(pipeline)) {
      return true;
    }
    // Fallback: any flow whose command is currently claimed/running on this device.
    const me = String(this.jivuCommand.getDeviceInstanceId() || '').toLowerCase();
    return this.jivuCommand.activeCommands().some(command =>
      String(command.targetDeviceInstanceId || '').toLowerCase() === me
      && (command.status === 'running' || command.status === 'claimed'));
  });

  readonly progressStyle = computed(() => {
    const progress = this.state().progress;
    return `${progress ?? 28}%`;
  });

  readonly statusLabel = computed(() => {
    switch (this.state().status) {
      case 'success':
        return 'Complete';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      case 'paused':
        return 'Paused';
      default:
        return 'Working';
    }
  });

  readonly statusMark = computed(() => {
    switch (this.state().status) {
      case 'success':
        return 'OK';
      case 'failed':
        return '!';
      case 'cancelled':
        return 'X';
      case 'paused':
        return 'II';
      default:
        return '';
    }
  });

  readonly canPause = computed(() => this.state().status === 'running' && Boolean(this.state().jobId));
  readonly canResume = computed(() => this.state().status === 'paused' && Boolean(this.state().jobId));
  readonly canCancel = computed(() =>
    (this.state().status === 'running' || this.state().status === 'paused') && Boolean(this.state().jobId));

  close(): void {
    this.loader.close(this.state().operationId ?? undefined);
  }

  toggleDock(): void {
    this.loader.toggleDock(this.state().operationId ?? undefined);
  }

  async pause(): Promise<void> {
    const state = this.state();
    if (this.controlBusy || !state.operationId || !state.jobId || state.status !== 'running') return;
    this.controlBusy = true;
    try {
      const result = await firstValueFrom(this.pipelineControl.pause(state.jobId, 'Paused from pipeline popup.'));
      this.loader.pause(state.operationId, {
        message: result?.message || 'Job paused.',
        progress: state.progress,
        jobId: state.jobId
      });
    } catch (error) {
      this.loader.update(state.operationId, {
        detail: this.safeError.message(error, { fallback: 'Unable to pause this job.' })
      });
    } finally {
      this.controlBusy = false;
    }
  }

  async resume(): Promise<void> {
    const state = this.state();
    if (this.controlBusy || !state.operationId || !state.jobId || state.status !== 'paused') return;
    this.controlBusy = true;
    try {
      const result = await firstValueFrom(this.pipelineControl.resume(state.jobId));
      this.loader.resume(state.operationId, {
        message: result?.message || 'Job resumed.',
        progress: state.progress,
        jobId: state.jobId
      });
    } catch (error) {
      this.loader.update(state.operationId, {
        detail: this.safeError.message(error, { fallback: 'Unable to resume this job.' })
      });
    } finally {
      this.controlBusy = false;
    }
  }

  async cancel(): Promise<void> {
    const state = this.state();
    if (this.controlBusy || !state.operationId || !state.jobId || !['running', 'paused'].includes(state.status)) return;
    this.controlBusy = true;
    try {
      const result = await firstValueFrom(this.pipelineControl.cancel(state.jobId, 'Cancelled from pipeline popup.'));
      this.loader.cancel(state.operationId, {
        message: result?.message || 'Job cancelled.',
        progress: state.progress,
        jobId: state.jobId
      });
    } catch (error) {
      this.loader.update(state.operationId, {
        detail: this.safeError.message(error, { fallback: 'Unable to cancel this job.' })
      });
    } finally {
      this.controlBusy = false;
    }
  }
}
